import { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePostManualTreeChapterEdit } from '../../../../../../api/manual-tree/manual-tree';
import { ErrorInfo, PostManualTreeChapterEditBody } from '../../../../../../api/model';
import { useDefaultOptions } from '../../../../../../hooks/api';
import ConfirmModal from '../../../../../modules/ConfirmModal';
import ErrorModal from '../../../../../modules/ErrorModal';
import Modal from '../../../../../modules/Modal';
import SpinnerModal from '../../../../../modules/SpinnerModal';

type Props = {
  chapterId: number;
  productionId: number;
  title: string | undefined;
  open: boolean;
  onClose: () => void;
};

function SectionChapterTitleModal({ chapterId, productionId, title, open, onClose }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);

  const [displayConfirm, setDisplayConfirm] = useState(false); // 確認ダイアログ

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PostManualTreeChapterEditBody>({
    defaultValues: {
      title: String(title),
    },
  });

  const {
    mutateAsync,
    isLoading,
    error,
    data: titleData,
    isSuccess,
  } = usePostManualTreeChapterEdit<AxiosError<ErrorInfo>, unknown>(options);
  const [displayError, setDisplayError] = useState(false);

  useEffect(() => {
    setDisplayError(error != null);
  }, [error]);

  const onSubmit = useCallback(() => {
    void handleSubmit(async (data: FieldValues) => {
      await mutateAsync({ chapterId, data });
    })();
  }, [chapterId, handleSubmit, mutateAsync]);

  useEffect(() => {
    if (isSuccess) {
      const basePath = process.env.REACT_APP_ROUTER_BASEPATH || '';
      window.location.href = `${basePath}/${language}/productions/sections/${productionId}/ps?section_id=${Number(
        titleData.data.section_id
      )}`;
    }
  }, [isSuccess, productionId, language, titleData]);

  return (
    <Modal active={open} additionalClassName="popupfr600" displayCloseButton onClose={onClose}>
      <form onSubmit={onSubmit}>
        <div className="popupfr pt20 popupfr600">
          <div className="teamsep2">
            <div>
              <label htmlFor="title">{t('章タイトル')}</label>
              <input
                type="text"
                id="title"
                style={{ width: '500px' }}
                className={errors.title?.type === 'required' ? 'error' : ''}
                {...register('title', { required: true })}
              />
              {errors.title?.type === 'required' && <p className="errormessage show">{t('タイトルは必須項目です')}</p>}
            </div>
          </div>

          <div className="btnfr btnfr2 mb0">
            <div>
              <span className="btn2 btnbg-g mr10">
                <span className="modal-close2" onClick={onClose}>
                  {t('キャンセル').toString()}
                </span>
              </span>
              <span>
                <input
                  type="button"
                  value={t('更新').toString()}
                  className="btn1 lsp1"
                  onClick={() => setDisplayConfirm(true)}
                />
              </span>
            </div>
          </div>
        </div>
      </form>

      {/* ユーザー引継ぎ確認 */}
      {displayConfirm && (
        <ConfirmModal
          onClickCancel={() => setDisplayConfirm(false)}
          onClickOk={() => {
            setDisplayConfirm(false);
            onSubmit();
          }}
        >
          {t('変更しますか?')}
        </ConfirmModal>
      )}

      {/* スピナーモーダル */}
      {isLoading && <SpinnerModal />}
      {/* エラー */}
      {displayError && <ErrorModal onClickOk={() => setDisplayError(false)} error={error} />}
    </Modal>
  );
}

export { SectionChapterTitleModal };
